import HomeCarousel from './HomeCarousel';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import ArrowLeftButton from '../elements/ArrowLeftButton';
import ArrowRightButton from '../elements/ArrowRightButton';
import { RecruitmentListData } from '@/components/domain/recruitment/types';
import { CONTENT_PADDING_X } from '@/styles/layout';
import Text from '../elements/Text';
import { useIsMobile } from '@/common/hooks';
import { RecruitmentBadges, RecruitmentCardText, MobileRecruitmentCardText, FavoriteClinicBadge, RecruitmentImage } from '@/components/domain/recruitment/RecruitmentGrid';

interface CardListCarouselProps {
  data: RecruitmentListData[];
  rowLength: number;
  arrowButton?: boolean;
  onClick?: (id: number) => void;
  type: "contents" | "image";
  onPageChange?: (page: number) => void;
  mobileRows?: 1 | 2;
  mobileFullWidthSingle?: boolean;
  resetKey?: string;
  /** 채용 목록의 PC 카드만 아래 일반 그리드의 폭·간격에 맞춘다. */
  desktopGridAligned?: boolean;
  homeControls?: boolean;
  onDesktopPageChange?: (page: number, total: number) => void;
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ScrollWrapper = styled.div`
  overflow: hidden;
`;

const CarouselTrack = styled.div<{ desktopGridAligned: boolean }>`
  display: flex;
  gap: ${props => props.desktopGridAligned ? '16px' : '0'};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

// Mobile paged grid (home premium uses one row).
const MobileWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: relative;
    width: 100%;
    padding-top: 0;
  }
`;

const MobileSwipeContainer = styled.div<{ singleRow: boolean }>`
  @media (max-width: 768px) {
    display: flex;
    align-items: ${props => props.singleRow ? 'flex-start' : 'stretch'};
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MobilePage = styled.div<{ fullWidth: boolean }>`
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    scroll-snap-align: center;
    display: grid;
    grid-template-columns: repeat(${props => props.fullWidth ? 1 : 2}, minmax(0, 1fr));
    gap: 0.5rem;
    padding: 0;
    box-sizing: border-box;
    flex-shrink: 0;
  }
`;

const MobileCard = styled.div`
  @media (max-width: 768px) {
    display: block;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-sizing: border-box;
    cursor: pointer;
  }
`;

const MobileCardImage = styled.div`
  position: relative;
  @media (max-width: 768px) {
    width: 100%;
    aspect-ratio: 16 / 9;
    flex-shrink: 0;
    overflow: hidden;

    img, & > [data-card-part="recruitment-image"] {
      width: 100%;
      height: 100% !important;
      aspect-ratio: auto !important;
      object-fit: cover;
    }
  }
`;

const CardContainer = styled.div<{ desktopGridAligned: boolean }>`
  width: ${props => props.desktopGridAligned ? 'calc((100% - 48px) / 4)' : '280px'};
  flex-shrink: 0;
  padding: ${props => props.desktopGridAligned ? '0' : '0 8px'};
  min-width: ${props => props.desktopGridAligned ? '0' : 'auto'};
  scroll-snap-align: start;

  /* 일반 목록의 MUI sm=6, md=3과 같은 PC 열 수다. */
  @media (min-width: 769px) and (max-width: 899.95px) {
    width: ${props => props.desktopGridAligned ? 'calc((100% - 16px) / 2)' : '280px'};
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin: 10px 0;
  cursor: pointer;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
`;

const ArrowContainer = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.position === 'left' ? 'left: 11px;' : 'right: -92px;'}
  z-index: 10;

  @media (max-width: 1280px) {
    display: none;
  }
`;

const CardListCarousel = ({ data, rowLength, arrowButton, type, onClick, onPageChange, mobileRows = 2, mobileFullWidthSingle = false, resetKey, desktopGridAligned = false, homeControls = false, onDesktopPageChange }: CardListCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  // 화살표가 보이는 PC 폭은 4열이다. 목록이 한 화면에 들어가면 넘김 버튼을 생략한다.
  const showDesktopArrows = arrowButton && (!desktopGridAligned || data.length > 4);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const firstCard = current.querySelector(':first-child') as HTMLElement;
      if (firstCard) {
        const gap = parseFloat(getComputedStyle(current).columnGap) || 0;
        const scrollAmount = firstCard.getBoundingClientRect().width + gap;
        if (direction === 'left') {
          current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        } else {
          current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    }
  };

  const mobilePageSize = mobileRows * 2;
  const fullWidth = mobileFullWidthSingle && data.length === 1;
  const dataKey = data.map(item => item.id).join(',');

  // 조건·공고 순서·개수가 바뀌면 PC와 모바일 모두 첫 장으로 돌아간다.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    if (mobileScrollRef.current) mobileScrollRef.current.scrollLeft = 0;
    setCurrentPage(1);
    onPageChange?.(1);
  }, [dataKey, mobilePageSize, onPageChange, resetKey]);

  // 모바일 페이지 크기는 홈 프리미엄만 옵션으로 변경한다.
  const mobilePages = [];
  if (data) {
    for (let i = 0; i < data.length; i += mobilePageSize) {
      mobilePages.push(data.slice(i, i + mobilePageSize));
    }
  }

  // 스크롤 이벤트로 현재 페이지 추적
  const handleMobileScroll = () => {
    if (mobileScrollRef.current) {
      const scrollLeft = mobileScrollRef.current.scrollLeft;
      const pageWidth = mobileScrollRef.current.offsetWidth;
      const page = Math.max(1, Math.min(mobilePages.length, Math.round(scrollLeft / pageWidth) + 1));
      setCurrentPage(page);
      if (onPageChange) {
        onPageChange(page);
      }
    }
  };

  return (
    <>
      {/* Desktop Carousel */}
      <CarouselContainer>
        {homeControls ? <HomeCarousel label="오늘의 채용" resetKey={`${resetKey ?? ''}:${dataKey}`} onPageChange={onDesktopPageChange}>
            {data.map((item) => (
              <div key={item.id}>
                <Card style={{ margin: 0, height: '100%' }} data-card="premium-pc" {...uiTargetAttrs(UI_KIND.RECRUITMENT, item.id)} onClick={() => onClick && onClick(item.id)}>
                  <div style={{ position: 'relative' }}>
                    <RecruitmentImage thumbnail={isMobile ? item.clinic.mobileThumbnail : item.clinic.pcThumbnail} title={item.title} />
                    <RecruitmentBadges info={item} />
                    <FavoriteClinicBadge show={item.isFavoriteClinic} isMobile={isMobile} />
                  </div>
                  {/* 글 칸은 홈 추천·채용 목록 카드와 같은 부품(글자 크기·간격 동일), 높이만 고정 */}
                  {type === "contents" && (
                    <RecruitmentCardText
                      clinic={item.clinic}
                      title={item.title}
                      jobTasks={item.jobTasks}
                      recruitmentTypes={item.recruitmentTypes}
                      welfares={item.welfares}
                      viewCount={item.viewCount}
                      favoriteCount={item.favoriteCount}
                      workDaysLabel={item.workDaysLabel}
                      infoChips={item.infoChips}
                      sameDayPay={item.sameDayPay}
                      payChip={item.payChip}
                      fixedHeight
                      iconCounts
                    />
                  )}
                </Card>
              </div>
            ))}
        </HomeCarousel> : <>
        {showDesktopArrows && (
          <ArrowContainer position="left">
            <ArrowRightButton onClick={() => handleScroll('left')} />
          </ArrowContainer>
        )}

        <ScrollWrapper>
          <CarouselTrack ref={scrollRef} desktopGridAligned={desktopGridAligned}>
            {data.map((item) => (
              <CardContainer key={item.id} desktopGridAligned={desktopGridAligned}>
                <Card data-card="premium-pc" {...uiTargetAttrs(UI_KIND.RECRUITMENT, item.id)} onClick={() => onClick && onClick(item.id)}>
                  <div style={{ position: 'relative' }}>
                    <RecruitmentImage thumbnail={isMobile ? item.clinic.mobileThumbnail : item.clinic.pcThumbnail} title={item.title} />
                    <RecruitmentBadges info={item} />
                    <FavoriteClinicBadge show={item.isFavoriteClinic} isMobile={isMobile} />
                  </div>
                  {/* 글 칸은 홈 추천·채용 목록 카드와 같은 부품(글자 크기·간격 동일), 높이만 고정 */}
                  {type === "contents" && (
                    <RecruitmentCardText
                      clinic={item.clinic}
                      title={item.title}
                      jobTasks={item.jobTasks}
                      recruitmentTypes={item.recruitmentTypes}
                      welfares={item.welfares}
                      viewCount={item.viewCount}
                      favoriteCount={item.favoriteCount}
                      workDaysLabel={item.workDaysLabel}
                      infoChips={item.infoChips}
                      sameDayPay={item.sameDayPay}
                      payChip={item.payChip}
                      fixedHeight
                      iconCounts
                    />
                  )}
                </Card>
              </CardContainer>
            ))}
          </CarouselTrack>
        </ScrollWrapper>

        {showDesktopArrows && (
          <ArrowContainer position="right">
            <ArrowLeftButton onClick={() => handleScroll('right')} />
          </ArrowContainer>
        )}</>}

      </CarouselContainer>

      {/* Mobile paged grid */}
      <MobileWrapper>
        <MobileSwipeContainer singleRow={mobileRows === 1} data-premium-track ref={mobileScrollRef} onScroll={handleMobileScroll}>
          {mobilePages.map((pageData, pageIndex) => (
            <MobilePage fullWidth={fullWidth} key={pageIndex} data-premium-page={pageIndex + 1}>
              {pageData.map((item) => (
                <MobileCard key={item.id} data-card="premium-mobile" {...uiTargetAttrs(UI_KIND.RECRUITMENT, item.id)} onClick={() => onClick && onClick(item.id)}>
                  <MobileCardImage>
                    <RecruitmentImage thumbnail={isMobile ? item.clinic.mobileThumbnail : item.clinic.pcThumbnail} title={item.title} />
                    <RecruitmentBadges info={item} />
                    <FavoriteClinicBadge show={item.isFavoriteClinic} isMobile />
                  </MobileCardImage>
                  <MobileRecruitmentCardText recruitment={item} stacked fullWidth={fullWidth} allowWelfareOverflow />
                </MobileCard>
              ))}
            </MobilePage>
          ))}
        </MobileSwipeContainer>
      </MobileWrapper>
    </>
  );
};

export default CardListCarousel;
